/**
 * Quote module API-client functions.
 *
 * Split from the main `api-client.ts` to keep the giant single-file pattern
 * from getting unmanageable. Same plumbing — `apiFetch` + zod parse on the
 * way out — different surface area.
 *
 * M-QUOTE.3.a per ADR-016.
 */
import {
  type CreateQuoteCatalogItemRequest,
  type CreateQuoteFollowupSequenceRequest,
  type CreateQuotePlaybookRequest,
  type CreateQuotePricingRuleRequest,
  type CreateQuoteServiceAreaRequest,
  type EnrollQuoteFollowupRequest,
  type QuoteCatalogItemResponse,
  QuoteCatalogItemResponseSchema,
  QuoteDocumentChunkResponseSchema,
  type QuoteDocumentResponse,
  QuoteDocumentResponseSchema,
  type QuoteFollowupSequenceDetailResponse,
  QuoteFollowupSequenceDetailResponseSchema,
  type QuoteDraftResponse,
  QuoteDraftResponseSchema,
  type QuoteFollowupEnrollmentResponse,
  QuoteFollowupEnrollmentResponseSchema,
  type QuoteFollowupSequenceResponse,
  QuoteFollowupSequenceResponseSchema,
  type QuotePlaybookResponse,
  QuotePlaybookResponseSchema,
  type QuotePricingRuleResponse,
  QuotePricingRuleResponseSchema,
  type QuoteServiceAreaResponse,
  QuoteServiceAreaResponseSchema,
  type RejectQuoteDraftRequest,
  type UpdateQuoteCatalogItemRequest,
  type UpdateQuotePlaybookRequest,
} from "@simpliwork/api-schema";
import { z } from "zod";
import { apiFetch, type GetTokenFn } from "./api-client";

const CatalogItemListSchema = z.object({ items: z.array(QuoteCatalogItemResponseSchema) });
const PricingRuleListSchema = z.object({ items: z.array(QuotePricingRuleResponseSchema) });
const ServiceAreaListSchema = z.object({ items: z.array(QuoteServiceAreaResponseSchema) });
const PlaybookListSchema = z.object({ items: z.array(QuotePlaybookResponseSchema) });
const DocumentListSchema = z.object({ items: z.array(QuoteDocumentResponseSchema) });
const DocumentChunkListSchema = z.object({ items: z.array(QuoteDocumentChunkResponseSchema) });
const DraftListSchema = z.object({ items: z.array(QuoteDraftResponseSchema) });
const SequenceListSchema = z.object({ items: z.array(QuoteFollowupSequenceResponseSchema) });
const EnrollmentListSchema = z.object({ items: z.array(QuoteFollowupEnrollmentResponseSchema) });

export type CatalogItemList = z.infer<typeof CatalogItemListSchema>;
export type PricingRuleList = z.infer<typeof PricingRuleListSchema>;
export type ServiceAreaList = z.infer<typeof ServiceAreaListSchema>;
export type PlaybookList = z.infer<typeof PlaybookListSchema>;
export type DocumentList = z.infer<typeof DocumentListSchema>;
export type DocumentChunkList = z.infer<typeof DocumentChunkListSchema>;
export type DraftList = z.infer<typeof DraftListSchema>;
export type SequenceList = z.infer<typeof SequenceListSchema>;
export type EnrollmentList = z.infer<typeof EnrollmentListSchema>;

type AuthOpt = { getToken?: GetTokenFn };

// ── catalog ────────────────────────────────────────────────────────────────

export async function fetchQuoteCatalogItems(opts: AuthOpt = {}): Promise<CatalogItemList> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/items", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return CatalogItemListSchema.parse(json);
}

export async function createQuoteCatalogItem(
  body: CreateQuoteCatalogItemRequest,
  opts: AuthOpt = {},
): Promise<QuoteCatalogItemResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/items", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteCatalogItemResponseSchema.parse(json);
}

export async function updateQuoteCatalogItem(
  id: string,
  body: UpdateQuoteCatalogItemRequest,
  opts: AuthOpt = {},
): Promise<QuoteCatalogItemResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/catalog/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteCatalogItemResponseSchema.parse(json);
}

export async function deleteQuoteCatalogItem(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/catalog/items/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── pricing rules ──────────────────────────────────────────────────────────

export async function fetchQuotePricingRules(opts: AuthOpt = {}): Promise<PricingRuleList> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/pricing-rules", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return PricingRuleListSchema.parse(json);
}

export async function createQuotePricingRule(
  body: CreateQuotePricingRuleRequest,
  opts: AuthOpt = {},
): Promise<QuotePricingRuleResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/pricing-rules", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuotePricingRuleResponseSchema.parse(json);
}

export async function deleteQuotePricingRule(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/catalog/pricing-rules/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── service areas ──────────────────────────────────────────────────────────

export async function fetchQuoteServiceAreas(opts: AuthOpt = {}): Promise<ServiceAreaList> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/service-areas", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return ServiceAreaListSchema.parse(json);
}

export async function createQuoteServiceArea(
  body: CreateQuoteServiceAreaRequest,
  opts: AuthOpt = {},
): Promise<QuoteServiceAreaResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/catalog/service-areas", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteServiceAreaResponseSchema.parse(json);
}

export async function deleteQuoteServiceArea(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/catalog/service-areas/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── playbooks ──────────────────────────────────────────────────────────────

export async function fetchQuotePlaybooks(opts: AuthOpt = {}): Promise<PlaybookList> {
  const json = await apiFetch<unknown>("/api/v1/quote/playbooks", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return PlaybookListSchema.parse(json);
}

export async function createQuotePlaybook(
  body: CreateQuotePlaybookRequest,
  opts: AuthOpt = {},
): Promise<QuotePlaybookResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/playbooks", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuotePlaybookResponseSchema.parse(json);
}

export async function updateQuotePlaybook(
  id: string,
  body: UpdateQuotePlaybookRequest,
  opts: AuthOpt = {},
): Promise<QuotePlaybookResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/playbooks/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuotePlaybookResponseSchema.parse(json);
}

export async function deleteQuotePlaybook(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/playbooks/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── documents ──────────────────────────────────────────────────────────────

export async function fetchQuoteDocuments(opts: AuthOpt = {}): Promise<DocumentList> {
  const json = await apiFetch<unknown>("/api/v1/quote/documents", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return DocumentListSchema.parse(json);
}

export async function fetchQuoteDocument(
  id: string,
  opts: AuthOpt = {},
): Promise<QuoteDocumentResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/documents/${id}`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteDocumentResponseSchema.parse(json);
}

export async function fetchQuoteDocumentChunks(
  id: string,
  opts: AuthOpt = {},
): Promise<DocumentChunkList> {
  const json = await apiFetch<unknown>(`/api/v1/quote/documents/${id}/chunks`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return DocumentChunkListSchema.parse(json);
}

export async function uploadQuoteDocument(
  file: File,
  documentType: string | null,
  opts: AuthOpt = {},
): Promise<QuoteDocumentResponse> {
  const form = new FormData();
  form.append("file", file);
  if (documentType) form.append("document_type", documentType);
  const json = await apiFetch<unknown>("/api/v1/quote/documents", {
    method: "POST",
    body: form,
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteDocumentResponseSchema.parse(json);
}

export async function deleteQuoteDocument(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/documents/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

// ── drafts ─────────────────────────────────────────────────────────────────

export async function fetchQuoteDraftsPending(opts: AuthOpt = {}): Promise<DraftList> {
  const json = await apiFetch<unknown>("/api/v1/quote/drafts/pending", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return DraftListSchema.parse(json);
}

export async function fetchQuoteDraftsAll(opts: AuthOpt = {}): Promise<DraftList> {
  const json = await apiFetch<unknown>("/api/v1/quote/drafts", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return DraftListSchema.parse(json);
}

export async function approveQuoteDraft(
  id: string,
  opts: AuthOpt = {},
): Promise<QuoteDraftResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/drafts/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({}),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteDraftResponseSchema.parse(json);
}

export async function rejectQuoteDraft(
  id: string,
  body: RejectQuoteDraftRequest,
  opts: AuthOpt = {},
): Promise<QuoteDraftResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/drafts/${id}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteDraftResponseSchema.parse(json);
}

// ── sequences + enrollments ───────────────────────────────────────────────

export async function fetchQuoteSequences(opts: AuthOpt = {}): Promise<SequenceList> {
  const json = await apiFetch<unknown>("/api/v1/quote/sequences", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return SequenceListSchema.parse(json);
}

export async function fetchQuoteSequence(
  id: string,
  opts: AuthOpt = {},
): Promise<QuoteFollowupSequenceDetailResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/sequences/${id}`, {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteFollowupSequenceDetailResponseSchema.parse(json);
}

export async function createQuoteSequence(
  body: CreateQuoteFollowupSequenceRequest,
  opts: AuthOpt = {},
): Promise<QuoteFollowupSequenceResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/sequences", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteFollowupSequenceResponseSchema.parse(json);
}

export async function deleteQuoteSequence(id: string, opts: AuthOpt = {}): Promise<void> {
  await apiFetch<unknown>(`/api/v1/quote/sequences/${id}`, {
    method: "DELETE",
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
}

export async function fetchQuoteEnrollments(opts: AuthOpt = {}): Promise<EnrollmentList> {
  const json = await apiFetch<unknown>("/api/v1/quote/enrollments", {
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return EnrollmentListSchema.parse(json);
}

export async function enrollQuoteFollowup(
  body: EnrollQuoteFollowupRequest,
  opts: AuthOpt = {},
): Promise<QuoteFollowupEnrollmentResponse> {
  const json = await apiFetch<unknown>("/api/v1/quote/enrollments", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteFollowupEnrollmentResponseSchema.parse(json);
}

export async function cancelQuoteEnrollment(
  id: string,
  opts: AuthOpt = {},
): Promise<QuoteFollowupEnrollmentResponse> {
  const json = await apiFetch<unknown>(`/api/v1/quote/enrollments/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({}),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return QuoteFollowupEnrollmentResponseSchema.parse(json);
}

// ── ai-draft request ──────────────────────────────────────────────────────

export async function requestQuoteDraft(
  body: { lead_id: string; workflow?: "draft_first_response" | "enrich" },
  opts: AuthOpt = {},
): Promise<{ enqueued: true }> {
  const json = await apiFetch<unknown>("/api/v1/quote/drafts/request", {
    method: "POST",
    body: JSON.stringify(body),
    ...(opts.getToken ? { getToken: opts.getToken } : {}),
  });
  return json as { enqueued: true };
}
